import { fetchTab } from "@/api/hooks/tab";
import type { ElementStyle, LocationInfo } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import type { RootState } from ".";

import { createWeatherTable } from "@/api/hooks/tools/weatherTable";
import { setAllToolLocation } from "@/reducer/TabsState";
import type { LabeledCoordinates, LocationCoordinates } from "@/types/location";
import { type WeatherTable, transformToWeatherTable } from "@/weatherTableView/types";
import Logger from "@mm/logging";
import { isEqual } from "lodash";
import { DateTime, Duration } from "luxon";
const logger = Logger.fromFilename(__filename);

export type WeatherTableState = WeatherTable & { exportCsvTrigger?: boolean; exportPdfTrigger?: boolean };

export type WeatherTablesState = {
  [id: number]: WeatherTableState;
};

const initialState: WeatherTablesState = {};

function omitWeatherTableExportTriggers(table: WeatherTableState): WeatherTable {
  const { exportCsvTrigger: _exportCsvTrigger, exportPdfTrigger: _exportPdfTrigger, ...weatherTable } = table;
  return weatherTable;
}

const weatherTableSlice = createSlice({
  name: "weatherTable",
  initialState: initialState as WeatherTablesState,
  reducers: {
    addWeatherTable(state, action: PayloadAction<WeatherTable>) {
      const existingTable = state[action.payload.id];
      const weatherTable = omitWeatherTableExportTriggers(action.payload as WeatherTableState);

      if (!existingTable) {
        state[action.payload.id] = weatherTable;
        return;
      }

      if (isEqual(omitWeatherTableExportTriggers(existingTable), weatherTable)) {
        return;
      }

      state[action.payload.id] = {
        ...weatherTable,
        exportCsvTrigger: existingTable?.exportCsvTrigger,
        exportPdfTrigger: existingTable?.exportPdfTrigger,
      };
    },
    pasteWeatherTable(state, action: PayloadAction<{ id: number; weatherTableGroups: WeatherTable["groups"] }>) {
      const { id, weatherTableGroups } = action.payload;
      const table = state[id];

      if (!table) {
        logger.error("Could not find weather table with id", id);
        return;
      }

      state[id] = { ...table, groups: weatherTableGroups, id };
    },
    discardWeatherTable(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      delete state[id];
    },
    setWeatherTableLocation(state, action: PayloadAction<{ id: number; props: LabeledCoordinates }>) {
      const { id, props } = action.payload;

      const weatherTable = state[id];
      if (!weatherTable) {
        logger.error("Could not find tool with id", id);
        return;
      }

      weatherTable.location = props;
    },
    setWeatherTableTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const weatherTable = state[id];
      if (!weatherTable) {
        logger.error("Could not find tool with id", id);
        return;
      }

      weatherTable.title = title;
    },
    setWeatherTableTitleStyle(state, action: PayloadAction<{ style: ElementStyle; id: number }>) {
      const { id, style } = action.payload;

      const weatherTable = state[id];
      if (!weatherTable) {
        logger.error("Could not find tool with id", id);
        return;
      }

      weatherTable.titleStyle = style;
    },
    setTableTimeRange(
      state,
      action: PayloadAction<{
        id: number;
        props: { datetimeRange: [string | null, string | null]; resolution: string; referenceTime: string };
      }>,
    ) {
      const {
        id,
        props: {
          datetimeRange: [from, to],
          resolution,
          referenceTime,
        },
      } = action.payload;

      const table = state[id];

      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.resolution = resolution;
      if (!table.timeLock) {
        table.datetimeFrom = null;
        table.datetimeTo = null;

        table.datetimeFromOffset = from ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO() : null;
        table.datetimeToOffset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      table.datetimeFrom = from;
      table.datetimeTo = to;
    },
    adjustAllTableDateTimeRange(state, action: PayloadAction<{ durationMinutes: number }>) {
      const { durationMinutes } = action.payload;
      for (const tableId in state) {
        const currentPlot = state[tableId];

        if (currentPlot.timeLock) {
          continue;
        }

        const { datetimeFrom, datetimeTo } = currentPlot;
        if (datetimeFrom) {
          const newFrom = DateTime.fromISO(datetimeFrom).plus({ minute: durationMinutes });
          const newTo = datetimeTo ? DateTime.fromISO(datetimeTo).plus({ minute: durationMinutes }) : null;
          currentPlot.datetimeFrom = newFrom.toISO();
          currentPlot.datetimeTo = newTo?.toISO() || null;
        }
      }
    },

    updateWeatherTableState(
      state,
      action: PayloadAction<{
        id: WeatherTable["id"];
        weatherTable: Partial<WeatherTable>;
      }>,
    ) {
      const { weatherTable, id } = action.payload;

      const table = state[id];

      if (!table) {
        logger.error("Could not find weather table with id", id);
        return;
      }

      const updatedWeatherTable = {
        ...table,
        ...weatherTable,
        id: id,
      };

      state[id] = updatedWeatherTable;
    },
    setWeatherTableTimeSettings(
      state,
      action: PayloadAction<{ id: number; timeLock: boolean; from: string; to: string; referenceTime: string }>,
    ) {
      const { id, timeLock, from, to, referenceTime } = action.payload;

      const weatherTable = state[id];

      if (!weatherTable) {
        logger.error("Could not find tool with id", id);
        return;
      }

      weatherTable.timeLock = timeLock;

      if (!timeLock) {
        weatherTable.datetimeFrom = null;
        weatherTable.datetimeFromOffset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;

        weatherTable.datetimeTo = null;
        weatherTable.datetimeToOffset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      weatherTable.datetimeFrom = weatherTable.datetimeFromOffset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(weatherTable.datetimeFromOffset)).toISO()
        : DateTime.fromISO(from).toISO();
      weatherTable.datetimeFromOffset = null;

      weatherTable.datetimeTo = weatherTable.datetimeToOffset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(weatherTable.datetimeToOffset)).toISO()
        : DateTime.fromISO(to).toISO();
      weatherTable.datetimeToOffset = null;
    },
    triggerWeatherTableExport(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportCsvTrigger = true;
    },

    resetWeatherTableExportFlag(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportCsvTrigger = false;
    },
    triggerWeatherTablePdfExport(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportPdfTrigger = true;
    },

    resetWeatherTablePdfExportFlag(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportPdfTrigger = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTab.fulfilled, (state, action) => {
      const tab = action.payload;
      for (const key in state) {
        delete state[+key];
      }
      for (const table of tab.weather_tables) {
        state[table.id] = transformToWeatherTable(table);
      }
    });
    builder.addCase(createWeatherTable.fulfilled, (state, action) => {
      const table = action.payload.tool;
      state[table.id] = transformToWeatherTable(table);
    });
    builder.addCase(setAllToolLocation, (state, action) => {
      const { lat, lon, label } = action.payload.location;
      for (const key in state) {
        const table = state[+key];
        if (!table) {
          logger.error("Could not find table with id", key);
          continue;
        }
        table.location = { lat, lon, label };
      }
    });
  },
});

const selectWeatherTables = (state: RootState) => state.tabGroup.present.weatherTables;

const makeSelectWeatherTablesByTabId = createSelector(
  [selectWeatherTables, (state: RootState, tabId: number) => tabId],
  (weatherTables, tabId) => Object.values(weatherTables).filter((table) => table.tabId === tabId),
);

export function useWeatherTables(tabId: number) {
  return useSelector((state: RootState) => makeSelectWeatherTablesByTabId(state, tabId));
}

const makeSelectWeatherTableById = createSelector(
  [selectWeatherTables, (state: RootState, id: number) => id],
  (weatherTables, id) => weatherTables[id],
);

export function useWeatherTable(id: number) {
  return useSelector((state: RootState) => makeSelectWeatherTableById(state, id));
}

export const {
  addWeatherTable,
  pasteWeatherTable,
  updateWeatherTableState,
  discardWeatherTable,
  setWeatherTableLocation,
  setWeatherTableTitle,
  setWeatherTableTitleStyle,
  setTableTimeRange,
  setWeatherTableTimeSettings,
  adjustAllTableDateTimeRange,
  resetWeatherTableExportFlag,
  resetWeatherTablePdfExportFlag,
  triggerWeatherTableExport,
  triggerWeatherTablePdfExport,
} = weatherTableSlice.actions;

export default weatherTableSlice.reducer;
